/* Atlas Our Story — mobile hero-only correction.
   <=749px only. Desktop Canva rendering is preserved exactly.
   Keeps lower story content intact and centers the OUR STORY title. */
(() => {
  const MOBILE_MAX = 749;
  const ASPECT = 16 / 9;
  let applying = false;
  let observer = null;
  let resizeTimer = null;
  let safariResizeObserver = null;
  const originals = new Map();
  const rect = (el) => (el ? el.getBoundingClientRect() : null);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  function remember(el) {
    if (!el || originals.has(el)) return;
    originals.set(el, el.getAttribute('style'));
  }

  function restoreDesktop() {
    originals.forEach((style, el) => {
      if (!el?.isConnected) return;
      if (style == null) el.removeAttribute('style');
      else el.setAttribute('style', style);
    });
    originals.clear();
  }

  function findHero(root) {
    return root?.querySelector('section') || null;
  }

  function findLargestMedia(hero) {
    const items = [...hero.querySelectorAll('video,img')].filter((el) => {
      const r = rect(el);
      return r && r.width > 120 && r.height > 80;
    });
    items.sort((a, b) => {
      const ar = rect(a), br = rect(b);
      return (br.width * br.height) - (ar.width * ar.height);
    });
    return items[0] || null;
  }

  function findTitleGroup(hero, heroWidth, heroHeight) {
    const leaf = [...hero.querySelectorAll('*')].find(
      (x) => x.children.length === 0 && x.textContent?.trim().toUpperCase() === 'OUR STORY'
    );
    if (!leaf) return null;

    let group = leaf;
    while (group && group !== hero) {
      const r = rect(group);
      if (r && r.width > heroWidth * 0.28 && r.width < heroWidth * 0.96 && r.height < heroHeight * 0.55) {
        return group;
      }
      group = group.parentElement;
    }
    return leaf;
  }


  function findVisibleTitleLeaves(hero) {
    return [...hero.querySelectorAll('*')]
      .filter((el) => {
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
        if (text !== 'OUR STORY') return false;
        return ![...el.children].some((child) =>
          (child.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase() === 'OUR STORY'
        );
      })
      .filter((el) => {
        const r = rect(el);
        const cs = getComputedStyle(el);
        return r && r.width > 100 && r.height > 20 && cs.display !== 'none' && cs.visibility !== 'hidden';
      })
      .sort((a, b) => rect(a).top - rect(b).top);
  }

  function applySafariTitleFix(hero, titleGroup) {
    if (!isSafari || window.innerWidth > MOBILE_MAX || !hero || !titleGroup) return;

    /* Safari/WebKit lays the Canva text-effect layers apart after the mobile
       hero is compacted. Nudge the title group up and hide only the lower
       duplicate leaf layer confirmed in Safari responsive testing. */
    titleGroup.style.setProperty('top', 'calc(50% - 35px)', 'important');

    const leaves = findVisibleTitleLeaves(hero);
    if (leaves.length >= 2) {
      const duplicate = leaves[leaves.length - 1];
      remember(duplicate);
      duplicate.style.setProperty('visibility', 'hidden', 'important');
    }
  }

  function syncSafariPageHeight() {
    if (!isSafari || window.innerWidth > MOBILE_MAX) return;

    const root = document.querySelector('#root');
    const content = document.querySelector('#canvify-content');
    const main = document.querySelector('#canvify-main');
    if (!root || !content || !main) return;

    const sections = [...root.querySelectorAll('section')];
    if (sections.length < 2) return;

    const rootTop = root.getBoundingClientRect().top;
    const requiredHeight = Math.max(
      ...sections.map((section) => section.getBoundingClientRect().bottom)
    ) - rootTop;

    if (!Number.isFinite(requiredHeight) || requiredHeight <= 0) return;

    [root, content, main].forEach((el) => {
      remember(el);
      const h = `${requiredHeight}px`;
      el.style.setProperty('height', h, 'important');
      el.style.setProperty('min-height', h, 'important');
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('overflow', 'hidden', 'important');
    });
  }

  function ensureSafariResizeObserver() {
    if (!isSafari || window.innerWidth > MOBILE_MAX || safariResizeObserver || !('ResizeObserver' in window)) return;
    const root = document.querySelector('#root');
    if (!root) return;

    safariResizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(syncSafariPageHeight);
    });
    [...root.querySelectorAll('section')].forEach((section) => safariResizeObserver.observe(section));
  }

  function fitHeroOnly() {
    if (applying) return;
    const root = document.querySelector('#root');
    if (!root) return;

    applying = true;
    if (observer) observer.disconnect();
    try {
      if (window.innerWidth > MOBILE_MAX) {
        if (safariResizeObserver) {
          safariResizeObserver.disconnect();
          safariResizeObserver = null;
        }
        restoreDesktop();
        return;
      }

      const hero = findHero(root);
      if (!hero) return;
      const mediaEl = findLargestMedia(hero);
      if (!mediaEl) return;

      const heroWidth = hero.parentElement?.clientWidth || root.clientWidth || window.innerWidth;
      const heroHeight = heroWidth / ASPECT;
      const h = `${heroHeight}px`;

      remember(hero);
      hero.style.setProperty('height', h, 'important');
      hero.style.setProperty('min-height', h, 'important');
      hero.style.setProperty('max-height', h, 'important');
      hero.style.setProperty('overflow', 'hidden', 'important');
      hero.style.setProperty('position', 'relative', 'important');

      let el = mediaEl.parentElement;
      while (el && el !== hero.parentElement) {
        if (hero.contains(el)) {
          remember(el);
          el.style.setProperty('height', h, 'important');
          el.style.setProperty('min-height', h, 'important');
          el.style.setProperty('max-height', h, 'important');
          el.style.setProperty('width', '100%', 'important');
          el.style.setProperty('top', '0', 'important');
          el.style.setProperty('left', '0', 'important');
          el.style.setProperty('transform', 'none', 'important');
        }
        if (el === hero) break;
        el = el.parentElement;
      }

      remember(mediaEl);
      mediaEl.style.setProperty('width', '100%', 'important');
      mediaEl.style.setProperty('height', '100%', 'important');
      mediaEl.style.setProperty('position', 'absolute', 'important');
      mediaEl.style.setProperty('top', '0', 'important');
      mediaEl.style.setProperty('left', '0', 'important');
      mediaEl.style.setProperty('transform', 'none', 'important');
      mediaEl.style.setProperty('object-fit', 'cover', 'important');
      mediaEl.style.setProperty('object-position', 'center center', 'important');

      const titleGroup = findTitleGroup(hero, heroWidth, heroHeight);
      if (titleGroup) {
        remember(titleGroup);
        titleGroup.style.setProperty('position', 'absolute', 'important');
        titleGroup.style.setProperty('top', '50%', 'important');
        titleGroup.style.setProperty('left', '50%', 'important');
        titleGroup.style.setProperty('transform', 'translate(-50%, -50%)', 'important');
        titleGroup.style.setProperty('width', '90%', 'important');
        titleGroup.style.setProperty('max-width', '720px', 'important');
        titleGroup.style.setProperty('margin', '0', 'important');
        titleGroup.style.setProperty('text-align', 'center', 'important');
        titleGroup.style.setProperty('z-index', '5', 'important');
      }

      applySafariTitleFix(hero, titleGroup);
      syncSafariPageHeight();
      ensureSafariResizeObserver();
    } finally {
      if (observer) observer.observe(root, { childList: true, subtree: true });
      applying = false;
    }
  }

  function repeat() {
    [0, 100, 250, 500, 900, 1500, 2500, 4000].forEach((d) =>
      setTimeout(() => requestAnimationFrame(fitHeroOnly), d)
    );
  }

  function startObserver() {
    const root = document.querySelector('#root');
    if (!root || observer) return;
    observer = new MutationObserver(() => {
      if (!applying) requestAnimationFrame(fitHeroOnly);
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function init() {
    repeat();
    setTimeout(startObserver, 400);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.addEventListener('load', repeat, { once: true });
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(repeat, 100);
  });
})();
